import React, { useEffect, useState, useCallback } from "react";
import {
    Box, Typography, TextField, MenuItem, Button, IconButton, Tooltip, FormHelperText, Autocomplete,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import segmentService from "../../../services/segmentService";
import {
    UNOMI_COMPARISON_OPERATORS,
    buildUnomiConditionFromRows,
    defaultEmptyRows,
    DEFAULT_UNOMI_PROPERTY_FIELDS,
    normalizePropertyId,
    isKnownProperty,
} from "../utils/unomiConditionUtils";
import ConditionValueEditor from "../../fidelite/components/shared/ConditionValueEditor";
import {
    applyFieldChange,
    applyOperatorChange,
    operatorsForSelect,
} from "../../fidelite/utils/conditionFieldUtils";

const UNOMI_OP_IDS = UNOMI_COMPARISON_OPERATORS.map((o) => o.id);

const genId = () => `u_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const selectMenuProps = {
    PaperProps: {
        style: { maxHeight: 320 },
        sx: { zIndex: 1600 },
    },
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    disableScrollLock: true,
};

function ConditionRow({ row, fields, onChange, onRemove, depth = 0 }) {
    if (row.type === "group") {
        return (
            <Box sx={{ pl: depth * 2, borderLeft: depth ? "2px solid #E5E7EB" : "none", mb: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <TextField
                        select size="small" label="Groupe"
                        value={row.groupType || "and"}
                        onChange={(e) => onChange({ ...row, groupType: e.target.value })}
                        sx={{ ...fieldSx, minWidth: 100 }}
                        SelectProps={{ MenuProps: selectMenuProps }}
                    >
                        <MenuItem value="and">ET</MenuItem>
                        <MenuItem value="or">OU</MenuItem>
                    </TextField>
                    <Tooltip title="Ajouter une condition">
                        <IconButton size="small" onClick={() => onChange({
                            ...row,
                            conditions: [
                                ...(row.conditions || []),
                                { id: genId(), type: "condition", property: fields[0]?.id || "", operator: "equals", value: "" },
                            ],
                        })}>
                            <AddCircleOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer le groupe">
                        <IconButton size="small" onClick={onRemove} sx={{ color: "#EF4444" }}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                {(row.conditions || []).map((child, idx) => (
                    <ConditionRow
                        key={child.id || idx}
                        row={child}
                        fields={fields}
                        depth={depth + 1}
                        onChange={(updated) => {
                            const next = [...(row.conditions || [])];
                            next[idx] = updated;
                            onChange({ ...row, conditions: next });
                        }}
                        onRemove={() => {
                            const next = (row.conditions || []).filter((_, i) => i !== idx);
                            onChange({ ...row, conditions: next });
                        }}
                    />
                ))}
            </Box>
        );
    }

    const propertyId = normalizePropertyId(row.property || "");
    const fieldMeta = fields.find((f) => f.id === propertyId);
    const isUnknownProperty = Boolean(propertyId && !isKnownProperty(propertyId));
    const allowedOpIds = operatorsForSelect(propertyId, UNOMI_OP_IDS, { payload_schema: null }, "unomi", fieldMeta, row.operator);
    const hideValue = row.operator === "exists";

    const applyUnomiPatch = (patched) => {
        const { field, ...rest } = patched;
        onChange({ ...row, ...rest, property: field ?? patched.property ?? row.property });
    };

    return (
        <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap" mb={1} sx={{ pl: depth * 2 }}>
            <Box sx={{ minWidth: 200, flex: 1 }}>
                <Autocomplete
                    size="small"
                    fullWidth
                    disableClearable
                    autoHighlight
                    options={fields}
                    getOptionLabel={(o) => o?.label || ""}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    value={fields.find((f) => f.id === propertyId) || fields[0] || null}
                    onChange={(_, newVal) => {
                        if (!newVal) return;
                        applyUnomiPatch(applyFieldChange(
                            { ...row, field: row.property },
                            newVal.id,
                            null,
                            UNOMI_OP_IDS,
                            "unomi",
                            newVal,
                        ));
                    }}
                    noOptionsText="Aucune propriété disponible"
                    renderInput={(params) => (
                        <TextField {...params} label="Propriété" placeholder="Rechercher une propriété…" />
                    )}
                    sx={fieldSx}
                    ListboxProps={{ style: { maxHeight: 320 } }}
                    slotProps={{ paper: { sx: { zIndex: 1600 } } }}
                />
                {isUnknownProperty && (
                    <FormHelperText error sx={{ mx: 1.75 }}>
                        « {propertyId.replace("properties.", "")} » ignorée (absente du catalogue)
                    </FormHelperText>
                )}
            </Box>
            <TextField
                select size="small" label="Opérateur"
                value={row.operator || "equals"}
                onChange={(e) => {
                    applyUnomiPatch(applyOperatorChange(
                        { ...row, field: row.property },
                        e.target.value,
                        {},
                        "unomi",
                        fieldMeta,
                    ));
                }}
                sx={{ ...fieldSx, minWidth: 160 }}
                SelectProps={{ MenuProps: selectMenuProps }}
            >
                {UNOMI_COMPARISON_OPERATORS
                    .filter((op) => allowedOpIds.includes(op.id))
                    .map((op) => (
                        <MenuItem key={op.id} value={op.id}>{op.label}</MenuItem>
                    ))}
            </TextField>
            {!hideValue && (
                <Box sx={{ flex: 1, minWidth: 180 }}>
                    <ConditionValueEditor
                        dialect="unomi"
                        field={propertyId}
                        operator={row.operator || "equals"}
                        value={row.value}
                        unomiField={fieldMeta}
                        onChange={(v) => onChange({ ...row, value: v })}
                    />
                </Box>
            )}
            <Tooltip title="Supprimer">
                <IconButton size="small" onClick={onRemove} sx={{ color: "#EF4444", mt: 0.5 }}>
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

/**
 * Visual builder for Unomi profilePropertyCondition trees (brand injected server-side).
 */
const UnomiConditionBuilder = ({ combinator, rows, onChange }) => {
    const [fields, setFields] = useState(DEFAULT_UNOMI_PROPERTY_FIELDS);

    const loadFields = useCallback(async () => {
        try {
            const items = await segmentService.getConditionFields();
            if (Array.isArray(items) && items.length > 0) {
                setFields(items);
            }
        } catch {
            // Built-in catalog is already shown; API may be unavailable on some environments.
        }
    }, []);

    useEffect(() => {
        loadFields();
    }, [loadFields]);

    const handleCombinatorChange = (val) => onChange({ combinator: val, rows });

    const updateRow = (idx, updated) => {
        const next = [...rows];
        next[idx] = updated;
        onChange({ combinator, rows: next });
    };

    const removeRow = (idx) => {
        onChange({ combinator, rows: rows.filter((_, i) => i !== idx) });
    };

    const addCondition = () => {
        onChange({
            combinator,
            rows: [
                ...rows,
                { id: genId(), type: "condition", property: fields[0]?.id || "", operator: "equals", value: "" },
            ],
        });
    };

    const addGroup = () => {
        onChange({
            combinator,
            rows: [
                ...rows,
                {
                    id: genId(),
                    type: "group",
                    groupType: "or",
                    conditions: [
                        { id: genId(), type: "condition", property: fields[0]?.id || "", operator: "equals", value: "" },
                    ],
                },
            ],
        });
    };

    return (
        <Box>
            {(rows || []).length > 1 && (
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography variant="body2" color="text.secondary">Combiner les critères avec :</Typography>
                    <TextField
                        select size="small"
                        value={combinator || "and"}
                        onChange={(e) => handleCombinatorChange(e.target.value)}
                        sx={{ ...fieldSx, minWidth: 100 }}
                        SelectProps={{ MenuProps: selectMenuProps }}
                    >
                        <MenuItem value="and">ET</MenuItem>
                        <MenuItem value="or">OU</MenuItem>
                    </TextField>
                </Box>
            )}

            {(rows || []).map((row, idx) => (
                <ConditionRow
                    key={row.id || idx}
                    row={row}
                    fields={fields}
                    onChange={(updated) => updateRow(idx, updated)}
                    onRemove={() => removeRow(idx)}
                />
            ))}

            <Box display="flex" gap={1} mt={1}>
                <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addCondition} sx={{ textTransform: "none" }}>
                    Ajouter une condition
                </Button>
                <Button size="small" variant="outlined" onClick={addGroup} sx={{ textTransform: "none" }}>
                    Ajouter un groupe OU/ET
                </Button>
            </Box>
        </Box>
    );
};

export default UnomiConditionBuilder;
export { buildUnomiConditionFromRows, defaultEmptyRows };
